import { useParams } from "react-router-dom";
import { fetchGet, transformFirebaseTime } from "../util/helpers";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Chip,
  Dialog,
  DialogContent,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  FacebookEmbed,
  InstagramEmbed,
  LinkedInEmbed,
  TikTokEmbed,
  YouTubeEmbed,
} from "react-social-media-embed";
import {
  IconBrandFacebook,
  IconBrandInstagram,
  IconBrandLinkedin,
  IconBrandTiktok,
  IconBrandYoutube,
  IconChevronLeft,
  IconChevronRight,
  IconX,
} from "@tabler/icons-react";
import dayjs from "dayjs";

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const platformIcon = {
  instagram: <IconBrandInstagram size={14} />,
  facebook: <IconBrandFacebook size={14} />,
  youtube: <IconBrandYoutube size={14} />,
  tiktok: <IconBrandTiktok size={14} />,
  linkedin: <IconBrandLinkedin size={14} />,
};

const platformColor = {
  instagram: "#C13584",
  facebook: "#1877F2",
  youtube: "#FF0000",
  tiktok: "#000",
  linkedin: "#0A66C2",
};

function detectPlatform(link) {
  if (!link) return null;
  if (link.includes("instagram")) return "instagram";
  if (link.includes("facebook")) return "facebook";
  if (link.includes("youtube") || link.includes("youtu.be")) return "youtube";
  if (link.includes("tiktok")) return "tiktok";
  if (link.includes("linkedin")) return "linkedin";
  return null;
}

function PostEmbed({ link }) {
  const platform = detectPlatform(link);
  if (platform === "instagram")
    return <InstagramEmbed url={link} width="100%" captioned />;
  if (platform === "facebook")
    return <FacebookEmbed url={link} width="100%" captioned />;
  if (platform === "youtube")
    return <YouTubeEmbed url={link} width="100%" />;
  if (platform === "tiktok")
    return <TikTokEmbed url={link} width={325} captioned />;
  if (platform === "linkedin")
    return <LinkedInEmbed url={link} width="100%" />;
  return (
    <Typography variant="body2" sx={{ color: "var(--body-text-color-500)" }}>
      {link}
    </Typography>
  );
}

function PostPill({ post, onClick, creatorName }) {
  const platform = detectPlatform(post.link);
  const color = platformColor[platform] || "var(--secondary-500)";
  const icon = platformIcon[platform] || null;
  const label = creatorName || platform || "Post";

  return (
    <Tooltip title={`${creatorName ? creatorName + " · " : ""}${platform || "Post"}`} arrow>
      <Box
        onClick={(e) => {
          e.stopPropagation();
          onClick(post);
        }}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          px: 1,
          py: 0.25,
          borderRadius: "6px",
          background: `${color}14`,
          border: `1px solid ${color}30`,
          color,
          cursor: "pointer",
          fontSize: "0.65rem",
          fontWeight: 600,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          maxWidth: "100%",
          transition: "all 0.15s ease",
          "&:hover": {
            background: `${color}22`,
            transform: "scale(1.03)",
          },
        }}
      >
        {icon}
        <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
          {label}
        </span>
      </Box>
    </Tooltip>
  );
}

function LiveContent({ matches = [] }) {
  const { campaignId } = useParams();
  const [postLinkList, setPostLinkList] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(dayjs());
  const [selectedPost, setSelectedPost] = useState(null);

  const creatorNameMap = useMemo(() => {
    const map = {};
    matches.forEach((m) => {
      if (m.userDocId && m.name) map[m.userDocId] = m.name;
    });
    return map;
  }, [matches]);

  const getAllDeliverables = useCallback(async () => {
    const results = await fetchGet(
      `/brand/deliverables?campaignId=${campaignId}`
    );
    if (results.result) {
      const postLinks = results.result.filter(
        (item) =>
          item.platform !== "draft" &&
          item.platform !== "concept" &&
          item.platform !== "creator-brief" &&
          item.platform !== "creator-agreement" &&
          item.link
      );
      console.log("published posts", postLinks);
      setPostLinkList(postLinks);

      const dates = postLinks
        .filter((p) => p.submittedAt)
        .map((p) => dayjs(transformFirebaseTime(p.submittedAt)))
        .filter((d) => d.isValid())
        .sort((a, b) => a.valueOf() - b.valueOf());
      if (dates.length > 0) {
        setCurrentMonth(dates[0].startOf("month"));
      }
    }
  }, [campaignId]);

  useEffect(() => {
    getAllDeliverables();
  }, [getAllDeliverables]);

  const postsByDate = useMemo(() => {
    const map = {};
    postLinkList.forEach((post) => {
      if (!post.submittedAt) return;
      const date = dayjs(transformFirebaseTime(post.submittedAt));
      if (!date.isValid()) return;
      const key = date.format("YYYY-MM-DD");
      if (!map[key]) map[key] = [];
      map[key].push(post);
    });
    return map;
  }, [postLinkList]);

  const calendarDays = useMemo(() => {
    const startOfMonth = currentMonth.startOf("month");
    const endOfMonth = currentMonth.endOf("month");
    const startDay = startOfMonth.day();
    const daysInMonth = endOfMonth.date();

    const days = [];
    for (let i = 0; i < startDay; i++) {
      days.push(null);
    }
    for (let d = 1; d <= daysInMonth; d++) {
      days.push(currentMonth.date(d));
    }
    const remaining = 7 - (days.length % 7);
    if (remaining < 7) {
      for (let i = 0; i < remaining; i++) {
        days.push(null);
      }
    }
    return days;
  }, [currentMonth]);

  const totalPosts = postLinkList.length;
  const platformCounts = useMemo(() => {
    const counts = {};
    postLinkList.forEach((p) => {
      const platform = detectPlatform(p.link);
      if (platform) counts[platform] = (counts[platform] || 0) + 1;
    });
    return counts;
  }, [postLinkList]);

  const today = dayjs().format("YYYY-MM-DD");

  return (
    <Box sx={{ width: "100%", px: 3, py: 2 }}>
      {/* Header */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography
            variant="h5"
            sx={{ fontWeight: 700, color: "var(--main-text-color)" }}
          >
            Content Calendar
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: "var(--body-text-color-500)", mt: 0.5 }}
          >
            {totalPosts} post{totalPosts !== 1 ? "s" : ""} published
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          {Object.entries(platformCounts).map(([platform, count]) => (
            <Chip
              key={platform}
              icon={platformIcon[platform]}
              label={`${count}`}
              size="small"
              sx={{
                height: 26,
                fontSize: "0.75rem",
                fontWeight: 600,
                background: `${platformColor[platform]}10`,
                color: platformColor[platform],
                border: `1px solid ${platformColor[platform]}25`,
                "& .MuiChip-icon": { color: platformColor[platform] },
              }}
            />
          ))}
        </Stack>
      </Stack>

      {/* Calendar */}
      <Box
        sx={{
          background: "#fff",
          borderRadius: "16px",
          border: "1px solid var(--neutral-200)",
          overflow: "hidden",
        }}
      >
        {/* Month navigation */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            px: 3,
            py: 2,
            background:
              "linear-gradient(135deg, rgba(159,37,246,0.04) 0%, rgba(92,164,251,0.04) 100%)",
            borderBottom: "1px solid var(--neutral-200)",
          }}
        >
          <IconButton
            size="small"
            onClick={() => setCurrentMonth((m) => m.subtract(1, "month"))}
            sx={{ color: "var(--body-text-color-500)" }}
          >
            <IconChevronLeft size={20} />
          </IconButton>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 700, color: "var(--main-text-color)" }}
          >
            {currentMonth.format("MMMM YYYY")}
          </Typography>
          <IconButton
            size="small"
            onClick={() => setCurrentMonth((m) => m.add(1, "month"))}
            sx={{ color: "var(--body-text-color-500)" }}
          >
            <IconChevronRight size={20} />
          </IconButton>
        </Stack>

        {/* Weekday headers */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(7, 1fr)",
            borderBottom: "1px solid var(--neutral-200)",
          }}
        >
          {WEEKDAYS.map((day) => (
            <Box
              key={day}
              sx={{
                py: 1,
                textAlign: "center",
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  color: "var(--body-text-color-500)",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  fontSize: "0.65rem",
                }}
              >
                {day}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Day cells */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(7, 1fr)",
          }}
        >
          {calendarDays.map((day, idx) => {
            if (!day) {
              return (
                <Box
                  key={`empty-${idx}`}
                  sx={{
                    minHeight: 90,
                    borderRight:
                      (idx + 1) % 7 !== 0
                        ? "1px solid var(--neutral-100)"
                        : "none",
                    borderBottom: "1px solid var(--neutral-100)",
                    background: "var(--neutral-50)",
                  }}
                />
              );
            }

            const dateKey = day.format("YYYY-MM-DD");
            const posts = postsByDate[dateKey] || [];
            const isToday = dateKey === today;
            const isCurrentMonth =
              day.month() === currentMonth.month();

            return (
              <Box
                key={dateKey}
                sx={{
                  minHeight: 90,
                  p: 0.75,
                  borderRight:
                    (idx + 1) % 7 !== 0
                      ? "1px solid var(--neutral-100)"
                      : "none",
                  borderBottom: "1px solid var(--neutral-100)",
                  background: posts.length > 0 ? "rgba(159,37,246,0.02)" : "#fff",
                  transition: "background 0.15s ease",
                  "&:hover": {
                    background: posts.length > 0 ? "rgba(159,37,246,0.05)" : "var(--neutral-50)",
                  },
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 24,
                    height: 24,
                    borderRadius: "50%",
                    fontWeight: isToday ? 800 : 500,
                    fontSize: "0.75rem",
                    color: isToday
                      ? "#fff"
                      : isCurrentMonth
                      ? "var(--main-text-color)"
                      : "var(--body-text-color-200)",
                    background: isToday ? "var(--secondary-500)" : "transparent",
                  }}
                >
                  {day.date()}
                </Typography>
                <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                  {posts.slice(0, 3).map((post, i) => (
                    <PostPill
                      key={post.id || i}
                      post={post}
                      onClick={setSelectedPost}
                      creatorName={creatorNameMap[post.userDocId]}
                    />
                  ))}
                  {posts.length > 3 && (
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: "0.6rem",
                        color: "var(--body-text-color-200)",
                        fontWeight: 600,
                        pl: 0.5,
                      }}
                    >
                      +{posts.length - 3} more
                    </Typography>
                  )}
                </Stack>
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* Post embed dialog */}
      <Dialog
        open={!!selectedPost}
        onClose={() => setSelectedPost(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "16px",
            overflow: "hidden",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: 2.5,
            py: 1.5,
            borderBottom: "1px solid var(--neutral-200)",
            background:
              "linear-gradient(135deg, rgba(159,37,246,0.04) 0%, rgba(92,164,251,0.04) 100%)",
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            {selectedPost &&
              platformIcon[detectPlatform(selectedPost.link)] && (
                <Box
                  sx={{
                    color:
                      platformColor[detectPlatform(selectedPost.link)],
                    display: "flex",
                  }}
                >
                  {platformIcon[detectPlatform(selectedPost.link)]}
                </Box>
              )}
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              {selectedPost && creatorNameMap[selectedPost.userDocId]
                ? creatorNameMap[selectedPost.userDocId]
                : ""}{selectedPost && creatorNameMap[selectedPost.userDocId] ? " · " : ""}
              {selectedPost
                ? detectPlatform(selectedPost.link)?.charAt(0).toUpperCase() +
                  detectPlatform(selectedPost.link)?.slice(1)
                : ""}{" "}
              Post
            </Typography>
            {selectedPost?.submittedAt && (
              <Typography
                variant="caption"
                sx={{ color: "var(--body-text-color-200)" }}
              >
                {dayjs(transformFirebaseTime(selectedPost.submittedAt)).format(
                  "MMM D, YYYY"
                )}
              </Typography>
            )}
          </Stack>
          <IconButton
            size="small"
            onClick={() => setSelectedPost(null)}
            sx={{ color: "var(--body-text-color-500)" }}
          >
            <IconX size={18} />
          </IconButton>
        </Box>
        <DialogContent
          sx={{
            display: "flex",
            justifyContent: "center",
            py: 3,
            minHeight: 300,
          }}
        >
          {selectedPost?.link && <PostEmbed link={selectedPost.link} />}
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default LiveContent;
